import "../styles/GlobalAuth.css";
import { useEffect, useState } from "react";
import { resetPasswordService } from "../../../services/auth/resetPasswordService";
import { useMessageContext } from "../../../hooks/UseMessageContext";
import { useNavigate } from "react-router-dom";
import LeftComponent from "./components/LeftComponent";
import AuthContainer from "../../global/AuthContainer";
import decode from "jwt-decode";

const PASSWORD_COMPLEXITY_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/;
const PASSWORD_HELPER_TEXT =
	"Password must contain at least 8 characters, including uppercase and lowercase letters, a number, and a special character.";

function EyeIcon() {
	return (
		<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
		</svg>
	);
}

function EyeOffIcon() {
	return (
		<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a20.6 20.6 0 0 1 4.22-5.06M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 7 11 7a20.6 20.6 0 0 1-2.34 3.31M14.12 14.12a3 3 0 1 1-4.24-4.24"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path d="M1 1l22 22" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
		</svg>
	);
}

export default function RecoverPassword() {
	const navigate = useNavigate();
	const { dispatch2 } = useMessageContext();
	const { messageArray } = useMessageContext();
	// The raw token is what actually gets sent to the backend for validation now (V2 flow).
	// It is still decoded client-side, but only to display which account the reset is for -
	// that decoded value is never sent back to the server.
	const [resetToken, setResetToken] = useState("");
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	// True whenever the reset link itself is the problem (malformed on load, or rejected by
	// the API as expired/invalid/unverifiable) - distinct from a form-validation issue like
	// a blank field or a weak password, which stay on the form via dispatchFunction toasts.
	const [linkInvalid, setLinkInvalid] = useState(false);

	useEffect(() => {
		try {
			const pathname = window.location.pathname;
			const result = pathname.slice(9);
			const decoded = decode(result);
			// jwt-decode only base64-decodes the payload - it neither verifies the signature nor
			// checks exp, so an expired link still parses cleanly here. The backend remains the
			// authority (parseAndValidate on /api/auth/reset-password); this check is UX only, so
			// the user is told the link is dead before typing a new password twice.
			if (!decoded.exp || decoded.exp * 1000 <= Date.now()) {
				setLinkInvalid(true);
				return;
			}
			setUsername(decoded.username);
			setResetToken(result);
		} catch {
			setLinkInvalid(true);
		}
	}, [navigate]);

	function dispatchFunction(request) {
		dispatch2({
			type: "MESSAGEARRAY",
			payload: [
				...messageArray,
				request === "error"
					? "Password not changed"
					: request === "same"
					? "Passwords not same"
					: request === "blank"
					? "Please give some input"
					: request === "weak"
					? PASSWORD_HELPER_TEXT
					: request === "success"
					? "Successfully changed password"
					: "Successfull message",
			],
		});
	}

	function passwordRecoveryFn() {
		if (password === "" || confirmPassword === "") {
			dispatchFunction("blank");
			return;
		}
		if (password !== confirmPassword) {
			dispatchFunction("same");
			return;
		}
		if (!PASSWORD_COMPLEXITY_REGEX.test(password)) {
			dispatchFunction("weak");
			return;
		}
		resetPasswordService(resetToken, password).then(function (response) {
			if (response && response.status === "successfull") {
				successfullFunction();
			} else {
				// Every failure mode the reset-password API can return at this point (invalid,
				// expired, malformed, or otherwise unverifiable token; account no longer found)
				// is a problem with the link itself, not with what the user typed - a generic
				// "Password not changed" toast was confusing since the user never got that far.
				setLinkInvalid(true);
			}
		});
	}

	const handleKeyDown = async (event) => {
		if (event.keyCode === 13) {
			passwordRecoveryFn();
		}
	};

	function successfullFunction() {
		setConfirmPassword("");
		setPassword("");
		dispatchFunction("success");
		navigate("/login");
	}

	if (linkInvalid) {
		return (
			<AuthContainer>
				<LeftComponent />
				<div className='RightComponentLoginFields'>
					<div className='RightComponentHeading'>
						<h2>Password Reset Link Expired</h2>
					</div>
					<div className='RightComponentInputFields'>
						<p className='AuthContainerBold'>
							Your password reset link is invalid or has expired.
						</p>
						<p className='AuthContainerBold'>
							For security reasons, password reset links are valid for only 10 minutes.
						</p>
						<p className='AuthContainerBold'>
							Please request a new password reset link to continue.
						</p>
						<button onClick={() => navigate("/forgot")}>
							Back to Forgot Password
						</button>
					</div>
				</div>
			</AuthContainer>
		);
	}

	return (
		<AuthContainer>
			<LeftComponent />
			{/* below division wraps all the login field */}
			<div className='RightComponentLoginFields'>
				{/* below division wraps the headings */}
				<div className='RightComponentHeading'>
					<h2>Create a New Password for {username}</h2>
					<p className='AuthContainerBold'>Enter and confirm your new password below.</p>
				</div>
				{/* below division wraps the two input fields */}
				<div className='RightComponentInputFields'>
					<div className='PasswordInputWrapper'>
						<input
							autoComplete='new-password'
							className='InputFieldClass'
							placeholder='Enter new password'
							type={showPassword ? "text" : "password"}
							value={password}
							onChange={function (event) {
								setPassword(event.target.value);
							}}
						/>
						<button
							type='button'
							className='PasswordToggleBtn'
							aria-label={showPassword ? "Hide password" : "Show password"}
							onClick={() => setShowPassword(!showPassword)}
						>
							{showPassword ? <EyeOffIcon /> : <EyeIcon />}
						</button>
					</div>
					<p className='PasswordHelperText'>{PASSWORD_HELPER_TEXT}</p>
					<div className='PasswordInputWrapper'>
						<input
							autoComplete='new-password'
							className='InputFieldClass'
							type={showConfirmPassword ? "text" : "password"}
							placeholder='Re-enter new password'
							onKeyDown={handleKeyDown}
							value={confirmPassword}
							onChange={function (event) {
								setConfirmPassword(event.target.value);
							}}
						/>
						<button
							type='button'
							className='PasswordToggleBtn'
							aria-label={showConfirmPassword ? "Hide password" : "Show password"}
							onClick={() => setShowConfirmPassword(!showConfirmPassword)}
						>
							{showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
						</button>
					</div>
					<button onClick={() => passwordRecoveryFn()}>
						Update Password
					</button>
				</div>
			</div>
		</AuthContainer>
	);
}
